import React, { useState, useEffect } from "react";
import { Collapse, Container, NavbarToggler, NavLink, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import Scrollspy from "react-scrollspy";
import { Link, useLocation } from "react-router-dom";
import { useProfile } from "../../../Components/Hooks/UserHooks";
import { modulePublicNavigation } from '../../../modules/registry';

// Import Images
import logodark from "../../../assets/images/logo-dark.png";
import logolight from "../../../assets/images/logo-light.png";

const Navbar = () => {
    const [isOpenMenu, setisOpenMenu] = useState(false);
    const [navClass, setnavClass] = useState("");
    const location = useLocation();

    /**
     * Whether anyone is signed in.
     *
     * This bar offered "Sign in" and "Sign Up" unconditionally, including to a
     * member who already was. Following a footer link out of the app therefore
     * looked like being logged out: the session was intact — pressing Back
     * proved it — but every signal on the page said otherwise.
     */
    const { userProfile, token } = useProfile();
    const signedIn = !!(userProfile && token);

    /**
     * Where a section link should point.
     *
     * #hero, #services, #how-it-works, #faqs and #contact are ids on the
     * landing page's own sections and exist nowhere else. This bar is also
     * rendered on the policy pages, on /how-scoring-works and now on every page
     * open to signed-out visitors -- and on all of those, every one of those
     * five links did nothing at all: the URL gained a fragment matching no
     * element and the page sat still.
     *
     * Off the landing page they become "/#section", which navigates home and
     * then scrolls. On the landing page they stay bare fragments, because
     * "/#hero" there is a full-page load of the page you are already on.
     */
    const onLanding = location.pathname === "/";
    const section = (id) => (onLanding ? `#${id}` : `/#${id}`);

    // Home is the exception: #hero is the top of the landing page, so off the
    // landing page plain "/" gets there just as well and leaves a clean URL.
    // "/#hero" put a fragment in the address bar that named an implementation
    // detail and pointed at the same place.
    const homeHref = onLanding ? "#hero" : "/";

    /**
     * The bar's own background.
     *
     * .navbar-landing is transparent on desktop until .is-sticky is added on
     * scroll, which is right over the landing hero and wrong everywhere else:
     * on a policy page the grey section background showed straight through it,
     * leaving a grey band above the white content whenever the page was at the
     * top.
     *
     * Off the landing page there is no hero to be transparent over, so the bar
     * is solid from the first pixel.
     */
    const barClass = onLanding ? navClass : "is-sticky";

    // Where to come back to after signing in. Stashed rather than passed along:
    // the backend appends /auth/callback to whatever redirect it is handed, so
    // that value has to stay an origin. OAuthCallback reads this on the way
    // back. Without it, signing in from a shared forecast landed on the
    // dashboard and lost the page the visitor came for.
    const rememberWhereWeWere = () => {
        try {
            sessionStorage.setItem(
                "postLoginRedirect",
                `${location.pathname}${location.search || ""}`
            );
        } catch {
            // A browser refusing session storage is not a reason to block
            // sign-in; it just lands on the dashboard instead.
        }
    };

    const toggle = () => setisOpenMenu(!isOpenMenu);

    useEffect(() => {
        window.addEventListener("scroll", scrollNavigation, true);
    });

    const scrollNavigation = () => {
        var scrollup = document.documentElement.scrollTop;
        if (scrollup > 50) {
            setnavClass("is-sticky");
        } else {
            setnavClass("");
        }
    }

    const [activeLink, setActiveLink] = useState();
    useEffect(() => {
        const activation = (event) => {
            const target = event.target;
            if (target) {
                target.classList.add('active');
                setActiveLink(target);
                if (activeLink && activeLink !== target) {
                    activeLink.classList.remove('active');
                }
            }
        };
        const defaultLink = document.querySelector('.navbar li.a.active');
        if (defaultLink) {
            defaultLink?.classList.add("active")
            setActiveLink(defaultLink)
        }
        const links = document.querySelectorAll('.navbar a');
        links.forEach((link) => {
            link.addEventListener('click', activation);
        });
        return () => {
            links.forEach((link) => {
                link.removeEventListener('click', activation);
            });
        };
    }, [activeLink]);

    return (
        <React.Fragment>
            <nav className={"navbar navbar-expand-lg navbar-landing fixed-top " + barClass} id="navbar">
                <Container>
                    <Link className="navbar-brand" to="/index">
                        <span className="logo logo-dark">
                            <span className="text-primary fw-bold fs-5">ForecastNest</span>
                        </span>
                        <span className="logo logo-light">
                            <span className="text-white fw-bold fs-5">ForecastNest</span>
                        </span>
                    </Link>

                    <NavbarToggler className="navbar-toggler py-0 fs-20 text-body" onClick={toggle} type="button" data-bs-toggle="collapse"
                        data-bs-target="#navbarSupportedContent" aria-controls="navbarSupportedContent"
                        aria-expanded="false" aria-label="Toggle navigation">
                        <i className="mdi mdi-menu"></i>
                    </NavbarToggler>

                    <Collapse
                        isOpen={isOpenMenu}
                        className="navbar-collapse"
                        id="navbarSupportedContent"
                    >
                        <Scrollspy
                            offset={-18}
                            items={[
                                "hero",
                                "services",
                                "how-it-works",
                                "pricing",
                                "faqs",
                                "contact",
                            ]}
                            currentClassName="active"
                            className="navbar-nav mx-auto mt-2 mt-lg-0"
                            id="navbar-example"
                        >
                            <li className="nav-item">
                                <NavLink href={homeHref}>Home</NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink href={section("services")}>What it does</NavLink>
                            </li>
                            {/* #features no longer exists — that section was split into
                                how-it-works, your-template and what-is-measured. */}
                            <li className="nav-item">
                                <NavLink href={section("how-it-works")}>How it works</NavLink>
                            </li>
                            {/* Position matters: Scrollspy maps these onto
                                `items` above by index, so this <li> and
                                "pricing" have to sit at the same place in both
                                lists or the wrong link lights up. */}
                            <li className="nav-item">
                                <NavLink href={section("pricing")}>Pricing</NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink href={section("faqs")}>FAQ</NavLink>
                            </li>
                            <li className="nav-item">
                                <NavLink href={section("contact")}>Contact</NavLink>
                            </li>
                        </Scrollspy>

                        {/* Outside the Scrollspy on purpose.
                            It maps its children onto `items` by position to
                            decide which one is active, so extra <li>s with no
                            matching anchor would shift that mapping and light up
                            the wrong link. Both items below leave this page
                            entirely rather than scrolling to a section on it. */}
                        <ul className="navbar-nav mt-2 mt-lg-0">
                            {/* Plain <a>, not <Link>: this leaves the app entirely
                                for the Blogger-hosted insights.forecastnest.com, so
                                it needs target="_blank" like any other external
                                link out of the site, not React Router. Placed
                                before Shared research so the long-form write-ups
                                get at least as much top-nav weight as the raw data
                                table -- the nav previously had no entry at all for
                                the blog, which only surfaced from a footer link. */}
                            <li className="nav-item">
                                <a href="https://insights.forecastnest.com" target="_blank" rel="noopener"
                                   className="nav-link fw-medium">
                                    Insights
                                </a>
                            </li>
                            <UncontrolledDropdown nav inNavbar>
                                <DropdownToggle nav caret className="fw-medium text-nowrap">Public forecast</DropdownToggle>
                                <DropdownMenu>
                                    <DropdownItem tag={Link} to="/research" active={location.pathname === '/research'} onClick={()=>setisOpenMenu(false)}>Shared research</DropdownItem>
                                    {modulePublicNavigation.map(item=><DropdownItem key={item.link} tag={Link} to={item.link} active={location.pathname === item.link.split('?')[0]} onClick={()=>setisOpenMenu(false)}>{item.label}</DropdownItem>)}
                                </DropdownMenu>
                            </UncontrolledDropdown>
                        </ul>

                        <div className="">
                            {signedIn ? (
                                // A way back in, not an invitation to do what
                                // has already been done. /index is where the
                                // app's own layout takes over.
                                <Link to="/index" className="btn btn-primary">
                                    Go to my research
                                </Link>
                            ) : (
                                <>
                                    <Link to="/login" onClick={rememberWhereWeWere}
                                          className="btn btn-link fw-medium text-decoration-none text-body">Sign
                                        in</Link>
                                    <Link to="/register" className="btn btn-primary">Sign Up</Link>
                                </>
                            )}
                        </div>
                    </Collapse>
                </Container>
            </nav>
        </React.Fragment>
    );
};

export default Navbar;
